import { $$, on } from "../../utils/dom";

interface FavoriteItem {
  contentId: number;
  name: string;
  url: string;
  language: string;
}

interface FavoritesResponse {
  items: FavoriteItem[];
}

const setFavorite = (button: HTMLButtonElement, isFavorite: boolean): void => {
  const { labelAdd, labelRemove } = button.dataset;
  const label = (isFavorite ? labelRemove : labelAdd) ?? "";

  button.classList.toggle("-active", isFavorite);
  button.setAttribute("aria-pressed", String(isFavorite));
  button.setAttribute("title", label);
};

const toggleFavorite = async (button: HTMLButtonElement): Promise<void> => {
  if (button.disabled) {
    return;
  }

  const wasFavorite = button.getAttribute("aria-pressed") === "true";
  const isFavorite = !wasFavorite;
  const { apiUrl, antiforgeryToken, contentId, language } = button.dataset;
  const errorRegion = button.parentElement?.querySelector<HTMLElement>(".js-pageFavoriteError");
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  if (errorRegion) {
    errorRegion.textContent = "";
  }
  setFavorite(button, isFavorite);

  try {
    if (!apiUrl || !antiforgeryToken || !contentId || !language) {
      throw new Error("Favorite request data is missing.");
    }

    const response = await fetch(
      `${apiUrl}/${encodeURIComponent(contentId)}?language=${encodeURIComponent(language)}`,
      {
        method: isFavorite ? "PUT" : "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          RequestVerificationToken: antiforgeryToken,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Favorite request failed.");
    }

    const result = (await response.json()) as FavoritesResponse;
    if (!Array.isArray(result.items)) {
      throw new Error("Favorite response is invalid.");
    }

    const isFavoriteInLanguage = result.items.some(
      (item) =>
        item.contentId === Number(contentId) &&
        item.language.toLowerCase() === language.toLowerCase()
    );

    document.querySelectorAll<HTMLButtonElement>(".js-pageFavorite").forEach((favoriteButton) => {
      if (favoriteButton.dataset.contentId === contentId) {
        setFavorite(favoriteButton, isFavoriteInLanguage);
      }
    });

    document.dispatchEvent(new CustomEvent("favorites:changed", { detail: { items: result.items } }));
  } catch {
    setFavorite(button, wasFavorite);
    if (errorRegion) {
      errorRegion.textContent = (wasFavorite ? button.dataset.errorRemove : button.dataset.errorSave) ?? "";
    }
  } finally {
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }
};

export const onContentLoaded = (): void => {
  $$(".js-pageFavorite")?.forEach((element) => {
    if (element instanceof HTMLButtonElement) {
      on(element, "click", () => {
        void toggleFavorite(element);
      });
    }
  });
};

if (process.env.NODE_ENV !== "development") {
  on(document, "DOMContentLoaded", onContentLoaded);
}
